import React, { useState, useEffect } from "react";
import { notification, Divider, Result } from "antd";
import Spin from "Components/Spinner";
import CustomButton from "Components/CustomButton";
import apiClient from "utils/apiHandler";
import { utmParams } from "utils/helper";
import { useTranslation } from "react-i18next";
const { utm_source, utm_medium, utm_campaign, utm_term, utm_content } = utmParams();

const LoadingSpinner = ({ size = 24 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" style={{ color: "#EB6B4C" }}>
    <style>{`.spinner_S1WN{animation:spinner_MGfb .8s linear infinite;animation-delay:-.8s}.spinner_Km9P{animation-delay:-.65s}.spinner_JApP{animation-delay:-.5s}@keyframes spinner_MGfb{93.75%,100%{opacity:.2}}`}</style>
    <circle className="spinner_S1WN" cx="4" cy="12" r="3" fill="currentColor" />
    <circle className="spinner_S1WN spinner_Km9P" cx="12" cy="12" r="3" fill="currentColor" />
    <circle className="spinner_S1WN spinner_JApP" cx="20" cy="12" r="3" fill="currentColor" />
  </svg>
);
const FreeCourseTeaser = ({ productData, data, actionHandler, step, customStyle }) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const orderResponse = await createOrder();
    if (!orderResponse.status) {
      return;
    }
    await createFreeCourseAccess();
  };

  const createOrder = async () => {
    try {
      const payload = {
        buyer: data.identification,
        products: data.purchase_products,
        purchase_id: data.purchase_id,
        main_product: data.main_product,
        coupon: data.coupon,
        pathname: window.location.pathname,
        origin: window.location.origin,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        free_resolution_waiver: data.identification?.free_resolution_checkbox || false,
        is_offer: false,
        is_teaser: true,
      };

      const order = await apiClient.post("/orders", { ...payload, method: "free", source: "checkout" });
      if (!order.status) {
        setIsLoading(false);
        setResponseMessage(t("teaser.unexpectedError"));
        setHasError(true);
        return { status: false, data: order.data };
      }
      return { status: true, data: order.data };
    } catch (error) {
      setIsLoading(false);
      setResponseMessage(t("teaser.unexpectedError"));
      setHasError(true);
      return { status: false, data: error };
    }
  };

  const createFreeCourseAccess = async () => {
    try {
      setResponseMessage("");
      setHasError(false);
      setIsLoading(true);
      const payload = {
        offer_id: data.price_option?.offer_id,
        product_id: data.main_product,
        purchase_id: data?.purchase_id,
        identification: data?.identification,
      };

      const freeCourseResponse = await apiClient.post("/orders/free-course-teaser", payload);
      if (!freeCourseResponse.status) {
        console.error("Error creating free course access", freeCourseResponse.data);
        const message = t(freeCourseResponse?.data?.message || "teaser.unexpectedError");
        notification.error({
          message: t("teaser.notificationErrorTitle"),
          description: message,
        });
        setResponseMessage(message);
        setHasError(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);

      setCompleted(true);

      // Error handling
    } catch (error) {
      setHasError(true);
      setIsLoading(false);
    }
  };

  return (
    <div className={completed === true ? "free-course-teaser-container-completed" : "free-course-teaser-container"}>
      {hasError === false && completed === false && (
        <div>
          <h2 className="text-muted">{t("teaser.preparing")}</h2>
          <span className="text-muted">{t("teaser.preparingSubtitle")}</span>
        </div>
      )}
      {hasError === true && isLoading === false && (
        <div>
          <h2 className="text-danger">{t("teaser.errorTitle")}</h2>
          <h3>{responseMessage}</h3>
          <Divider dashed />
          <span className="text-muted">{t("teaser.errorHint")}</span>
          <div style={{ marginTop: "20px" }}>
            <CustomButton buttonStyle={customStyle} type="primary" onClick={() => init()}>
              {t("teaser.retry")}
            </CustomButton>
          </div>
        </div>
      )}
      {isLoading === false && hasError === false && completed === true && (
        <div>
          <Result
            status="success"
            title={t("teaser.successTitle")}
            subTitle={
              <div style={{ marginTop: "20px" }}>
                <strong>{t("teaser.successEmail")}</strong>
                <br />
                <div className="free-course-teaser-email-container">
                  <code className="free-course-teaser-email">{data?.identification?.email}</code>
                </div>
              </div>
            }
          />
        </div>
      )}
      <div style={{ marginTop: "55px" }}>
        <Spin size="large" spinning={isLoading} />
      </div>
    </div>
  );
};

export default FreeCourseTeaser;
